import { BlockedDate } from '../components/BlockedDatesForm/BlockedDatesForm';
import CONFIG from '../config/config';
import { MeetingType } from '../shared/meetingTypeToTranslationKey';
import { Customer } from './CustomersAPI';
import { instanceWith as APIClient } from './SimpleAPIClient';

export interface ReviewablePendingBookDto {
    id: number;
    customerName: string;
    meetingType: string;
    date: string;
    time: string;
    customerData?: Record<string, string>;
    paid: boolean;
    requiresPayment: boolean;
    trackingLink: string;
    isRefundable?: boolean;
    refundAmount?: string;
    refundStatus?: string;
    rejectedAt?: string;
}

export type Meeting = {
    id?: number,
    customerName: string,
    customerSurname: string,
    startTime: string,
    endTime: string,
    type: MeetingType,
    weddingDate: string | null,
    reminder: boolean,
    disable: boolean,
    phone: string | null,
    lastUpdateTime: string | null,
    notes: string | null,
    title: string | null,
    email?: string,
    customer?: Customer,
    customerId?: number | null,
    reservationId?: string,
    bookedMeetingType?: string,
    noShow?: boolean,
    exclusive?: boolean,
    inboundChannel?: string,
    removalReason?: MeetingRemovalReason | null,
    removalReasonNote?: string | null,
    bookingPlanId?: number | null,
}

export type UnavailableDate = {
    id?: number,
    start: number,
    end: number,
}

export type PendingBookingDto = {
    id: number,
    reservationReference: string,
    customerName: string,
    meetingType: string,
    date: string,
    time: string,
    customerData: Record<string, string>,
    paid: boolean,
    requiresPayment: boolean,
    trackingLink: string,
    refundable: boolean,
    refundAmount?: string | null,
    refundStatus?: string,
}

type AvailabilityResponse = {
    matches: boolean
}
  
type BodyApiResponse = Meeting[];

export const MEETING_REMOVAL_REASONS = ['BRIDE_CANCELLED', 'RESCHEDULE', 'OTHER'] as const;
export type MeetingRemovalReason = (typeof MEETING_REMOVAL_REASONS)[number];

export const MEETING_SMS_USE_CASES = ['new_appointment_booked', 'time_update', 'cancelation'] as const;
export type MeetingSmsUseCase = (typeof MEETING_SMS_USE_CASES)[number];

export const FREE_SMS_LIMIT_REACHED_CODE = 'FREE_SMS_LIMIT_REACHED';
export type FreeSmsLimitReachedResponse = { code: string; message: string };

const MeetingsAPI = {
    get: (startDate: string, endDate: string, type?: string, status?: string) => 
        APIClient(CONFIG.API_ENDPOINT).get<BodyApiResponse>(
          `/v1/meetings?startTime=${startDate}&endTime=${endDate}${type ? `&type=${type}` : ''}${status ? `&status=${status}` : ''}`
        ),
    pendings: (type: string = 'pending') => APIClient(CONFIG.API_ENDPOINT).get<{ meetings: ReviewablePendingBookDto[] }>(`/v1/meetings/pending?type=${type}`),
    import: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return APIClient(CONFIG.API_ENDPOINT).post<any>('/v1/meetings/import', formData);
    },
    accept: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<PendingBookingDto>(`/v1/meetings/pending/${id}/accept`),
    checkBooking: (id: number) => APIClient(CONFIG.API_ENDPOINT).get<boolean>(`/v1/meetings/${id}/booking`),
    reject: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<PendingBookingDto>(`/v1/meetings/pending/${id}/reject`),
    checkAvailability: (date: string, slotSizeMinutes: number = 30, daysRange: number = 2) => APIClient(CONFIG.API_ENDPOINT).get<AvailabilityResponse>(`/v1/meetings/availability?date=${date}&slotSizeMinutes=${slotSizeMinutes}&daysRange=${daysRange}`),
    search: (text: string) => APIClient(CONFIG.API_ENDPOINT).get<BodyApiResponse>(`v1/meetings?searchText=${text}`),
    getUnavailableDates: (startDate: string, endDate: string) => APIClient(CONFIG.API_ENDPOINT).get<UnavailableDate[]>(`/v1/blockedDates?start=${startDate}&end=${endDate}`),
    add: (meeting: Meeting) => APIClient(CONFIG.API_ENDPOINT).post<BodyApiResponse>(`/v1/meetings`, meeting),
    block: (blockedDates: BlockedDate) => APIClient(CONFIG.API_ENDPOINT).post<BodyApiResponse>(`/v1/blockedDates`, blockedDates),
    unblock: (id: number) => APIClient(CONFIG.API_ENDPOINT).delete<BodyApiResponse>(`/v1/blockedDates/${id}`),
    update: (id: number, meeting: Meeting) => APIClient(CONFIG.API_ENDPOINT).put<BodyApiResponse>(`/v1/meetings/${id}`, meeting),
    markNoShow: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<Meeting>(`/v1/meetings/${id}/no-show`),
    restore: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<Meeting>(`/v1/meetings/${id}/restore`),
    delete: (id: number, employee?: string, payload?: { reason: MeetingRemovalReason; note?: string }) =>
        APIClient(CONFIG.API_ENDPOINT).delete<BodyApiResponse>(
            `/v1/meetings/disable/${id}${employee ? `?workerName=${encodeURIComponent(employee)}` : ''}`,
            payload ? { data: payload } : undefined
        ),
    processRefund: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<PendingBookingDto>(`/v1/meetings/pending/${id}/process-refund`),
    rejectRefund: (id: number) => APIClient(CONFIG.API_ENDPOINT).post<PendingBookingDto>(`/v1/meetings/pending/${id}/reject-refund`),
    updatePaymentStatus: (id: number, paid: boolean) => APIClient(CONFIG.API_ENDPOINT).post<PendingBookingDto>(`/v1/meetings/pending/${id}/payment-status`, { paid }),
    getBookingByReservationId: (customerMeetingId: string, reservationId: string) => APIClient(CONFIG.API_ENDPOINT).get<PendingBookingDto>(`/v1/meetings/${customerMeetingId}/booking/${reservationId}`),
    sendSms: (meetingId: number, useCase: MeetingSmsUseCase) =>
        APIClient(CONFIG.API_ENDPOINT).post<void>(`/v1/meetings/${meetingId}/notifications/send-sms?useCase=${encodeURIComponent(useCase)}`),
};

export default MeetingsAPI;
  